import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { SearchInput, Select } from "@/components/ui/Form.jsx";
import { Callout, StatCard } from "@/components/ui/Display.jsx";
import { BarChart } from "@/components/charts/index.jsx";
import CurrentlyWorking from "@/components/dashboard/CurrentlyWorking.jsx";
import PeopleList, { NoEntry, StartedAt } from "@/components/dashboard/PeopleList.jsx";
import WorkIdleTable from "@/components/dashboard/WorkIdleTable.jsx";
import TopApps from "@/components/dashboard/TopApps.jsx";
import AutoRefresh from "@/components/dashboard/AutoRefresh.jsx";

import { timeEntryService, usageService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { DATE_PRESETS, deriveDashboard, rangeFor } from "@/lib/dashboard.js";
import {
    formatDate,
    formatDuration,
    formatHHmm,
    formatHoursTick,
    formatMinutes,
} from "@/lib/format.js";
import { downloadCsv } from "@/lib/csv.js";
import { niceHourMax } from "@/lib/chartTone.js";

/*
 * Replaces frontend/app/(dashboard)/dashboard/page.js and its eight components.
 *
 * The old page hand-rolled its fetching: three useState/useEffect fetchers, a
 * loading flag each, a 120-second setInterval countdown and a localStorage
 * auto-refresh toggle — roughly 120 lines before any UI. All of it is
 * refetchInterval here, which also does something the old timer did not:
 * refetchIntervalInBackground is false by default, so a dashboard left open on a
 * background tab stops polling instead of hitting the API every two minutes
 * forever.
 *
 * The three requests stay INDEPENDENT, as they were. The trend and top-apps
 * cards are slower and are allowed to arrive late rather than holding up the
 * numbers everyone actually watches.
 */

const REFRESH_MS = 120_000;

const STATUS_FILTERS = [
    { value: "all", label: "All users" },
    { value: "active", label: "Active users" },
    { value: "inactive", label: "Inactive users" },
];

export default function Dashboard() {
    const [preset, setPreset] = useState("today");
    /*
     * ACTIVE by default, as the page it replaces did.
     *
     * A deactivated account cannot start work, so on a live work dashboard it
     * can only ever appear under "Didn't start work" — permanently, inflating
     * the count and burying the people the list is actually about. The filter
     * stays, so an admin can go and look at the inactive ones deliberately.
     */
    const [status, setStatus] = useState("active");
    const [tableSearch, setTableSearch] = useState("");
    const [autoRefresh, setAutoRefresh] = useState(() => {
        // Same key the Next.js page used, so the preference survives the swap.
        try {
            return JSON.parse(localStorage.getItem("dashboard_auto_refresh") ?? "true");
        } catch {
            return true;
        }
    });

    const range = useMemo(() => rangeFor(preset), [preset]);
    const refetchInterval = autoRefresh ? REFRESH_MS : false;

    const [main, trend, apps] = useQueries({
        queries: [
            {
                queryKey: ["dashboard", "activity", range, status],
                queryFn: () =>
                    usageService
                        .getAllUsersActivityDashboard({
                            ...range,
                            status: status === "all" ? undefined : status,
                            role: "employee",
                        })
                        .then((r) => r.data?.data ?? {}),
                refetchInterval,
            },
            {
                queryKey: ["dashboard", "trend", range],
                queryFn: () =>
                    timeEntryService.getWorkHoursTrend(range).then((r) => r.data?.data ?? {}),
                refetchInterval,
            },
            {
                queryKey: ["dashboard", "topApps", range],
                queryFn: () =>
                    usageService
                        .getOrganizationTopApps({ ...range, limit: 10 })
                        .then((r) => r.data?.data ?? {}),
                refetchInterval,
            },
        ],
    });

    const d = useMemo(() => deriveDashboard(main.data), [main.data]);

    const toggleAutoRefresh = (on) => {
        setAutoRefresh(on);
        try {
            localStorage.setItem("dashboard_auto_refresh", JSON.stringify(on));
        } catch {
            // Private mode: the toggle still works for this session.
        }
    };

    const refreshAll = () => {
        main.refetch();
        trend.refetch();
        apps.refetch();
    };

    const busy = main.isFetching || trend.isFetching || apps.isFetching;

    /*
     * Export the table as it stands.
     *
     * Client-side on purpose: /api/export/* takes a SAVED report id, so there is
     * nothing to point it at for "the view I am looking at". The rows are
     * already here, so this needs no request at all.
     */
    const exportCsv = () =>
        downloadCsv(
            `work-and-idle-${range.startDate}_to_${range.endDate}`,
            ["Name", "Email", "Team", "Started", "Late", "Work (h)", "Idle (h)", "Idle %", "Productive %"],
            d.rows.map((r) => [
                r.name,
                r.email,
                r.team ?? "",
                r.started ? new Date(r.start).toLocaleString() : "",
                r.late ? "yes" : "no",
                (r.workMs / 3600000).toFixed(2),
                (r.idleMs / 3600000).toFixed(2),
                r.idlePct.toFixed(1),
                r.productivity,
            ]),
        );

    return (
        <>
            <PageChrome breadcrumb={["Dashboard"]} title="Active Users Dashboard">
                {/* The topbar carries only what is about the PAGE'S FRESHNESS.
                    The filters belong beside the heading, next to the numbers
                    they change — see the page head below. */}
                <>
                    <AutoRefresh
                        enabled={autoRefresh}
                        onToggle={toggleAutoRefresh}
                        intervalMs={REFRESH_MS}
                        lastUpdated={main.dataUpdatedAt}
                    />

                    <Button variant="primary" onClick={refreshAll} loading={busy} data-refresh>
                        <Icon name="clock" className="size-4" />
                        Refresh
                    </Button>
                </>
            </PageChrome>

            <main className="space-y-5 p-4 sm:p-5">
                {/* Page head — the h1 lives here, not in the topbar breadcrumb.
                    A page whose only title is a <span> inside a nav gives a
                    screen reader nothing to jump to. */}
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="text-[22px] font-semibold tracking-tight">
                            Active Users Dashboard
                        </h1>
                        <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                            Live work status across your organization
                            {main.dataUpdatedAt > 0 && (
                                <>
                                    {" · last synced "}
                                    <span className="font-medium text-slate-700 dark:text-slate-300">
                                        {new Date(main.dataUpdatedAt).toLocaleTimeString(undefined, {
                                            hour: "numeric",
                                            minute: "2-digit",
                                            second: "2-digit",
                                        })}
                                    </span>
                                </>
                            )}
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2.5">
                        <div className="inline-flex gap-0.5 rounded-md bg-slate-200/70 p-0.5 dark:bg-slate-800">
                            {Object.entries(DATE_PRESETS).map(([key, p]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setPreset(key)}
                                    data-preset={key}
                                    aria-pressed={preset === key}
                                    className={
                                        preset === key
                                            ? "rounded bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-900 shadow-xs dark:bg-slate-700 dark:text-white"
                                            : "rounded px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                                    }
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <span className="btn pointer-events-none">
                            <Icon name="calendar" className="size-4" />
                            {formatDate(range.endDate)}
                        </span>

                        <Select
                            size="sm"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            aria-label="Filter users"
                            className="w-auto"
                        >
                            {STATUS_FILTERS.map((f) => (
                                <option key={f.value} value={f.value}>
                                    {f.label}
                                </option>
                            ))}
                        </Select>

                        <Button onClick={exportCsv} disabled={!d.rows.length} data-export>
                            <Icon name="download" className="size-4" />
                            Export
                        </Button>
                    </div>
                </div>

                {main.isError && (
                    <Callout tone="danger">
                        {apiErrorMessage(main.error, "Failed to load the activity dashboard.")}{" "}
                        <button
                            type="button"
                            onClick={() => main.refetch()}
                            className="font-semibold underline"
                        >
                            Try again
                        </button>
                    </Callout>
                )}

                {/* ---- Stat cards ---- */}
                <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
                    <StatCard
                        tone="emerald"
                        label="Today Attendance"
                        icon={<Icon name="check" className="size-4" />}
                        value={
                            <>
                                {d.attended}{" "}
                                <span className="text-sm font-medium text-slate-400 dark:text-slate-500">
                                    / {d.teamSize}
                                </span>
                            </>
                        }
                        hint={`${d.teamSize ? Math.round((d.attended / d.teamSize) * 100) : 0}% of the team checked in`}
                    />
                    <StatCard
                        tone="slate"
                        label="Absent from Desk"
                        icon={<Icon name="userRound" className="size-4" />}
                        value={d.awayCount}
                        hint="No input for 10+ minutes"
                    />
                    <StatCard
                        tone="amber"
                        label="Late Starters"
                        icon={<Icon name="clock" className="size-4" />}
                        value={d.late.length}
                        hint={`Started after ${formatHHmm(d.lateStartTime)}`}
                    />
                    <StatCard
                        tone="slate"
                        label="Didn't Start Work"
                        icon={<Icon name="xCircle" className="size-4" />}
                        value={d.notStarted.length}
                        hint="No time entry in this range"
                    />
                    <StatCard
                        tone="emerald"
                        label="Total Work Time"
                        icon={<Icon name="clock" className="size-4" />}
                        value={formatDuration(d.totalWorkMs)}
                        hint={`Avg ${formatDuration(d.avgWorkMs)} per attending user`}
                    />
                    <StatCard
                        tone="rose"
                        label="Total Idle Time"
                        icon={<Icon name="pause" className="size-4" />}
                        value={formatDuration(d.totalIdleMs)}
                        hint={`${d.idleShare}% of tracked time`}
                    />
                </section>

                {/* ---- Currently working ---- */}
                <Card>
                    <CardHead>
                        <CardTitle
                            icon={<Icon name="check" className="size-4 text-emerald-500" />}
                            hint="Presence is measured from the last keyboard or mouse event"
                        >
                            Currently working
                            <CountBadge tone="emerald">{d.working.length} online</CountBadge>
                        </CardTitle>
                        <CardExtra>
                            <Link
                                to="/reports"
                                className="btn-link flex items-center gap-1"
                            >
                                Timeline report
                                <Icon name="arrowRight" className="size-3.5" />
                            </Link>
                        </CardExtra>
                    </CardHead>
                    <CardBody>
                        {main.isPending ? (
                            <Skeleton height={160} />
                        ) : (
                            <CurrentlyWorking people={d.working} />
                        )}
                    </CardBody>
                </Card>

                {/* ---- Late / not started ---- */}
                <section className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                    <Card>
                        <CardHead>
                            <CardTitle
                                icon={<Icon name="clock" className="size-4 text-amber-500" />}
                            >
                                Started later than {formatHHmm(d.lateStartTime)}
                            </CardTitle>
                            <CardExtra>
                                <CountBadge tone="amber">{d.late.length} people</CountBadge>
                            </CardExtra>
                        </CardHead>
                        <CardBody>
                            {main.isPending ? (
                                <Skeleton height={160} />
                            ) : (
                                <PeopleList
                                    people={d.late}
                                    empty="Nobody started late."
                                    right={(person) => <StartedAt person={person} />}
                                />
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHead>
                            <CardTitle
                                icon={<Icon name="xCircle" className="size-4 text-slate-400" />}
                            >
                                Didn't start work
                            </CardTitle>
                            <CardExtra>
                                <CountBadge tone="slate">{d.notStarted.length} people</CountBadge>
                            </CardExtra>
                        </CardHead>
                        <CardBody>
                            {main.isPending ? (
                                <Skeleton height={160} />
                            ) : (
                                <PeopleList
                                    people={d.notStarted}
                                    empty="Everyone has started."
                                    right={() => <NoEntry />}
                                />
                            )}
                        </CardBody>
                    </Card>
                </section>

                {/* ---- Trend + top apps ----
                    1.55fr / 1fr, as in the markup. A 2:1 split left the apps
                    column narrow enough to squeeze the name against its
                    productivity tag. */}
                <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
                    <Card>
                        <CardHead>
                            <CardTitle
                                hint={
                                    trend.data?.granularity === "hour"
                                        ? "Work hours logged in each hour of the day"
                                        : "Work hours logged on each day"
                                }
                            >
                                Work Hours Trend
                            </CardTitle>
                        </CardHead>
                        <CardBody>
                            {trend.isPending ? (
                                <Skeleton height={300} />
                            ) : (
                                <BarChart
                                    data={(trend.data?.trend ?? []).map((b) => ({
                                        label: b.label,
                                        value: b.totalHours,
                                    }))}
                                    /* Short on the axis, hours AND minutes in
                                       the tooltip — "3h" alone rounded a whole
                                       half hour of work out of the reading. */
                                    format={formatHoursTick}
                                    tooltipFormat={(v) => formatMinutes(v * 60)}
                                    niceMaxOf={niceHourMax}
                                    height={300}
                                    emptyMessage="No work recorded in this range"
                                />
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHead>
                            <CardTitle>Top Apps &amp; Browsers</CardTitle>
                            <CardExtra>
                                <Link to="/reports" className="btn-link">
                                    View all
                                </Link>
                            </CardExtra>
                        </CardHead>
                        {/* No CardBody: the list's rows carry their own padding
                            and dividers so they span the card — see TopApps. */}
                        {apps.isPending ? (
                            <CardBody>
                                <Skeleton height={200} />
                            </CardBody>
                        ) : (
                            <TopApps apps={apps.data?.topApps ?? []} />
                        )}
                    </Card>
                </section>

                {/* ---- Work & idle table ---- */}
                <Card>
                    <CardHead>
                        <CardTitle>Work &amp; Idle Hours by User</CardTitle>
                        <CardExtra>
                            <SearchInput
                                value={tableSearch}
                                onChange={(e) => setTableSearch(e.target.value)}
                                placeholder="Search user…"
                                aria-label="Search users"
                                className="w-full sm:w-64"
                            />
                        </CardExtra>
                    </CardHead>
                    <CardBody>
                        {main.isPending ? (
                            <Skeleton height={320} />
                        ) : (
                            <WorkIdleTable rows={d.rows} search={tableSearch} />
                        )}
                    </CardBody>
                </Card>
            </main>
        </>
    );
}

const BADGE = {
    emerald: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400",
    amber: "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400",
    slate: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
};

/** The count pill beside a card title, e.g. "14 online". */
function CountBadge({ tone = "slate", children }) {
    return (
        <span className={`ml-2 rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${BADGE[tone]}`}>
            {children}
        </span>
    );
}

/** Holds a card's height while its query is in flight, so nothing jumps. */
function Skeleton({ height }) {
    return (
        <div
            data-skeleton
            className="w-full animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800/60"
            style={{ height }}
            aria-hidden="true"
        />
    );
}
